import { Form, Formik, FormikProps } from "formik";
import { Modal } from "react-bootstrap";
import { Button, ThemeProviderV2 } from '@blueprint/ui';
import { useModal } from "react-modal-hook";

import { useSelector } from "../../../redux/hooks";
import { currentRolesSelector } from "../../../redux/slices/auth/selectors";
import { useProfileListByRole } from "../../../api/profile";
import { useUserList } from "../../../api/users";
import { Loader } from "../../../common/components/core";
import { SelectField } from "../../../common/components/form";
import { SelectOption } from "../../../common/components/form/Select";
import { getUserRoleOptionsByProfileType } from "../../../utils/role";
import { parseUserProfileTypeToText } from "../../../utils/userProfile";
import { useEffect, useRef, useState } from "react";
import { OptionsType } from "react-select";

type filterHandler = {
    show: () => void;
    hide: () => void;
};

export type FilterUserManagementListFormValues = {
    emails: string[];
    profileType: string;
    roleType: string;
    profileName: string;
    pageNo: number;
};

const useFilterHandler = (
    currentFilter: FilterUserManagementListFormValues,
    onSubmit: (value: FilterUserManagementListFormValues) => void,
): filterHandler => {
    const currentRoles = useSelector(currentRolesSelector);
    const { data: profileList = [] } = useProfileListByRole();
    const { data: userList = [] } = useUserList();
    const [profileType, setProfileType] = useState<string>(
        currentFilter.profileType,
    );
    const [ , setProfileName] = useState<string>(
        currentFilter.profileName,
    );
    const filterFormRef =
        useRef<FormikProps<FilterUserManagementListFormValues>>(null);
    const roleOptions = getUserRoleOptionsByProfileType(profileType, currentRoles);

    const profileTypeOptions = profileList
        .filter(
            (item, index, array) =>
                array.findIndex((item2) => item.type === item2.type) === index,
        )
        .map((item) => ({
            value: item.type,
            label: parseUserProfileTypeToText(item.type),
        }));
    const sortedProfileNameOptions = profileList
        .filter(
            (item, index, array) => {
                if (profileType) {
                    return item.type === profileType;
                } else {
                    return true;
                }
            }
        )
        .map((item) => ({
            value: item.name,
            label: item.name,
        }));


    const onResetHandler = () => {
        onSubmit({
            emails: [],
            profileType: "",
            roleType: "",
            profileName: "",
            pageNo: 0,
        });
        setProfileType("");
        hide();
    };

    // Reset user role value when profileType does not contain selected role
    useEffect(() => {
        const currentRole = filterFormRef.current?.values?.roleType;
        const profileTypeIncludeCurrentRole = !!roleOptions.find(
            (role) => role.value === currentRole,
        );

        !profileTypeIncludeCurrentRole &&
            filterFormRef.current?.setFieldValue("roleType", "");
    }, [profileType, roleOptions]);

    const onChangeProfileType = (
        profile: SelectOption | OptionsType<SelectOption> | null,
    ) => {
        filterFormRef.current?.setFieldValue("profileName", "");
        const isSelectOptionType = (variant: any): variant is SelectOption => {
            return !!variant?.value && !!variant?.label;
        };
        isSelectOptionType(profile) && setProfileType(profile.value);
    };

    const handleHide = () => {
        setProfileType(currentFilter.profileType);
        setProfileName(currentFilter.profileName);
        hide();
    };

    const [show, hide] = useModal(() => {
        return (
            <ThemeProviderV2>
            <Modal show onHide={handleHide} backdrop={true} centered>
                <Formik
                    innerRef={filterFormRef}
                    validateOnBlur={false}
                    validateOnChange={false}
                    onSubmit={(values) => {
                        onSubmit(values);
                        hide();
                    }}
                    initialValues={{
                        ...currentFilter,
                    }}
                >
                    {({ handleSubmit, isSubmitting, values }) => (
                        <Loader isLoading={isSubmitting}>
                            <Modal.Header>
                                <h2 className="mb-0">Filter</h2>
                            </Modal.Header>
                            <Modal.Body>
                                <Form
                                    autoComplete="off"
                                    noValidate
                                    onSubmit={handleSubmit}
                                >
                                    <SelectField
                                        name="emails"
                                        label="Email"
                                        isMulti
                                        options={userList.map((data) => ({
                                            value: data.email,
                                            label: data.email,
                                        }))}
                                        menuPosition="fixed"
                                    />
                                    <SelectField
                                        name="profileType"
                                        label="Profile Type"
                                        menuPosition="fixed"
                                        options={profileTypeOptions}
                                        onChange={onChangeProfileType}
                                    />
                                    <SelectField
                                        name="roleType"
                                        label="Role"
                                        options={roleOptions}
                                        menuPosition="fixed"
                                    />
                                    <SelectField
                                        name="profileName"
                                        label="Profile Name"
                                        menuPosition="fixed"
                                        options={sortedProfileNameOptions}
                                    />
                                    <Modal.Footer className="justify-content-between px-0 pb-0 pt-4">
                                        <Button
                                            variant="light.tertiary"
                                            onClick={onResetHandler}
                                        >
                                            Reset
                                        </Button>
                                        <Button variant="light.primary" type="submit">
                                            Apply
                                        </Button>
                                    </Modal.Footer>
                                </Form>
                            </Modal.Body>
                        </Loader>
                    )}
                </Formik>
            </Modal>
            </ThemeProviderV2>
        );
    }, [currentFilter, profileList, userList, profileType]);

    return {
        show,
        hide,
    };
};

export default useFilterHandler;
